import React from "react";

import type { ScatterDataPoint } from "chart.js";
import type { DateTime } from "luxon";

export enum Locale {
  deAT,
  enAT,
  deDE,
  enDE
}

export enum Environment {
  LOCAL,
  DEV,
  UAT,
  PROD,
}

// Response type from GEM_FUNDLIST API
type FundlistAPI = Array<{
  FUND_NAME_LONG: string;
  SHARES: Array<{
    MASTER_SHARE_CERTIFICATE: boolean;
    SHARE_ISIN: string;
    SHARE_ISIN_DISPLAY: string;
    AS_I__ID: number;
    DAT_MIN: number;
  }>;
}>;

type JSONFundlistAPI = {
  DATA: FundlistAPI;
};

// Response type from GEM_GRAPHICDATA_PERFORMANCE API
type GraphicDataPerformanceJson = FixedLengthArray<
  [
    {
      chart: {
        series: [
          {
            name: string;
            data: PerformanceDataJson
          },
        ];
      };
    },
  ]
>;

type PerformanceDataJson = FixedLengthArray<[number, number, string, string, string, string, string, string, number]>[];

type GraphicDataPerformance = {
  chart: {
    series: [
      {
        name: string;
        data: PerformanceData[]
      },
    ];
  };
}

interface PerformanceData {
  timestamp: number;
  performance: number;
  fee: number;
}

// Response type from GEM_FACTSHEET API
type JSONFactsheetAPI = {
  WARNINGS: Array<{
    ISIN: string | null;
    WARNINGS: string[];
  }>;
  WARNINGS_META: Array<{
    TITLE_MESSAGE: string | null;
  }>;
};

type Fund = {
  id: number;
  masterISIN: string;
  label: string;
  isins: string;
} | null;

type TableData = {
  masterISIN: string;
  label: string;
  finalAmount: number | null;
  totalPerformance: number | null;
};

type ShortWarning = {
  label: string;
  isHidden: boolean;
  message: React.ReactNode | null;
};

type ComplianceWarning = {
  masterISIN: string;
  warning: string[];
  title: string | null;
};

type DepositData = {
  deposit: (number | ScatterDataPoint | null)[];
  dates: DateTime[];
};

// Fixed length array type implementation
type ArrayLengthMutationKeys = "splice" | "push" | "pop" | "shift" | "unshift" | number;
type ArrayItems<T extends Array<any>> = T extends Array<infer TItems> ? TItems : never; // eslint-disable-line @typescript-eslint/no-explicit-any
type FixedLengthArray<T extends any[]> = // eslint-disable-line @typescript-eslint/no-explicit-any
  Pick<T, Exclude<keyof T, ArrayLengthMutationKeys>> & {
    [Symbol.iterator]: () => IterableIterator<ArrayItems<T>>;
  };

export type {
  JSONFundlistAPI,
  GraphicDataPerformanceJson,
  PerformanceDataJson,
  JSONFactsheetAPI,
  Fund,
  TableData,
  ShortWarning,
  ComplianceWarning,
  FixedLengthArray,
  DepositData,
  GraphicDataPerformance,
  PerformanceData
};
